import { z } from 'zod';
import { timestampSchema } from './shared';

// Legacy coarse buckets. Still written to `inquiry_type` so existing admin
// filters, CSV exports, and notifier templates keep reading a stable value —
// but NO LONGER a closed enum on the wire. `contact_page.engagementTypes` is
// admin-editable free text, so validating against a frozen union here meant an
// admin adding a fifth option produced a silent 400 on every submit.
export const INQUIRY_TYPES = ['Consulting', 'Speaking', 'Mentorship', 'Other'] as const;

// Spec: contact_form.fields[organisation_type].options.
export const ORGANISATION_TYPES = [
  'Bank/PSP',
  'Fintech',
  'Founder/Platform',
  'Investor/Board',
  'Public Sector',
  'Innovation/Training',
  'Other',
] as const;

export const leadRowSchema = z.object({
  id: z.string().min(1),
  fullName: z.string(),
  email: z.string(),
  mobile: z.string(),
  organisation: z.string().nullable(),
  inquiryType: z.string().nullable(),
  // Qualification + attribution (migration 0020). Nullable everywhere: the
  // chat-widget and newsletter lead paths send none of them.
  role: z.string().nullable(),
  organisationType: z.string().nullable(),
  serviceInterest: z.string().nullable(),
  countryOrMarkets: z.string().nullable(),
  desiredTiming: z.string().nullable(),
  sourcePage: z.string().nullable(),
  ctaId: z.string().nullable(),
  consent: z.boolean(),
  contacted: z.boolean(),
  contactedAt: timestampSchema.nullable(),
  notes: z.string().nullable(),
  source: z.string().nullable(),
  userAgent: z.string().nullable(),
  ipAddress: z.string().nullable(),
  createdAt: timestampSchema,
});

// Shared mobile rule — optional, but must look like a phone number when given.
const mobileField = z
  .string()
  .max(40)
  .optional()
  .transform((v) => (v ?? '').trim())
  .refine(
    (v) => v === '' || /^[+\d][\d\s\-().]{2,}$/.test(v),
    { message: 'mobile must be a valid phone number when provided' },
  );

// Public POST body for /api/leads. This is the single source of truth — the
// route imports it rather than keeping its own copy (the two had already
// drifted on `conversationId` bounds before they were merged).
export const leadCaptureSchema = z
  .object({
    fullName: z.string().min(1).max(200),
    email: z.string().email().max(320),
    mobile: mobileField,
    organisation: z.string().max(200).optional(),
    // Free text, not an enum — see INQUIRY_TYPES above.
    inquiryType: z.string().max(80).optional(),
    role: z.string().max(120).optional(),
    organisationType: z.string().max(80).optional(),
    serviceInterest: z.string().max(120).optional(),
    countryOrMarkets: z.string().max(200).optional(),
    desiredTiming: z.string().max(120).optional(),
    // Attribution. `sourcePage` is the path the form was submitted from;
    // `ctaId` is the `?offer=` / `?path=` value the visitor arrived with.
    sourcePage: z.string().max(200).optional(),
    ctaId: z.string().max(120).optional(),
    message: z.string().min(1).max(5000).optional(),
    consent: z.boolean().optional(),
    source: z.string().optional(),
    conversationId: z.string().min(1).max(200).optional(),
    // Honeypot — visible only to bots. Any non-empty value triggers a silent
    // 201 that doesn't touch the DB. See the leads.ts route handler.
    website: z.string().optional(),
  })
  // Explicit consent is required for the public contact form only. The chat
  // widget, newsletter CTA, and CSV import paths have their own consent
  // handling and would break under a blanket rule.
  .refine(
    (d) => d.source !== 'contact_form' || d.consent === true,
    { message: 'consent is required', path: ['consent'] },
  );

// Admin PUT body for /api/leads/:id.
export const leadUpdateSchema = z.object({
  contacted: z.boolean().optional(),
  notes: z.string().nullable().optional(),
});

export type LeadRow = z.infer<typeof leadRowSchema>;
export type LeadCapture = z.input<typeof leadCaptureSchema>;
export type LeadUpdate = z.input<typeof leadUpdateSchema>;
